import { AnyBreakpoints } from 'application/theme'
import { useElementWidth } from 'lib/use-element-size'
import React, { FC, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Slide } from 'ui/slider'
import { SliderContextProvider } from 'ui/slider'
import { SliderContent } from 'ui/slider/SliderContent'

import { getProjectName } from '../../lib/get-project-name'
import { useCarDetailsCardConfig } from '../../stages/car-details'
import { NextArrow, PrevArrow } from './Arrows'
import { useGetPropsForBreakpoint } from './useGetPropsForBreakpoint'
import { useSliderTranslation } from './useSliderTranslation'

export type Props = {
  slidesPerPage?: AnyBreakpoints<number> | number
  slidesPerGroup?: AnyBreakpoints<number> | number
  showArrows?: boolean
  activeGroup?: AnyBreakpoints<number> | number
  paddingForOverflow?: string | number
  onActiveGroupChange?(value: number): void
  arrowPosition?: string
  isSwipingDisabled?: boolean
  showOverflowedSlides?: boolean
}

export const Slider: FC<Props> = ({
  slidesPerPage: slidesPerPageProp = 1,
  slidesPerGroup: slidesPerGroupProp = slidesPerPageProp,
  showArrows = true,
  activeGroup: activeGroupProp = 0,
  onActiveGroupChange,
  paddingForOverflow,
  arrowPosition,
  isSwipingDisabled = false,
  showOverflowedSlides = false,
  children
}) => {
  const [isMoving, setIsMoving] = useState(false)
  const sliderContentRef = useRef<HTMLDivElement>(null)
  const pageWidth = useElementWidth(sliderContentRef)
  const { gallery } = useCarDetailsCardConfig()

  let slidesCount = useMemo(() => {
    let count = 0
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Slide) {
        count++
      }
    })
    return count
  }, [children])

  const theme = useTheme()

  const { slidesPerPage, slidesPerGroup, activeGroup } = useGetPropsForBreakpoint({
    slidesPerPageProp,
    slidesPerGroupProp,
    activeGroupProp
  })

  const slideWidth = pageWidth === null ? null : Math.ceil(pageWidth / slidesPerPage)
  const groupWidth = slideWidth === null ? null : slideWidth * slidesPerGroup

  const { shiftSlider, isShiftPrevPossible, isShiftNextPossible, state, maxTranslation } = useSliderTranslation({
    slidesCount,
    slideWidth,
    slidesPerGroup,
    onActiveGroupChange,
    slidesPerPage,
    activeGroup
  })

  const isCompactView = ['phone', 'phablet', 'tablet'].includes(theme.activeBreakpoint)

  return (
    <SliderContextProvider value={{ isMoving, setIsMoving }}>
      <Wrapper showOverflowedSlides={showOverflowedSlides}>
        <SliderContent
          onShiftRequest={shiftSlider}
          state={state}
          groupWidth={groupWidth}
          slidesPerPage={slidesPerPage}
          slideWidth={slideWidth}
          maxTranslation={maxTranslation}
          paddingForOverflow={paddingForOverflow}
          ref={sliderContentRef}
          isSwipingDisabled={isSwipingDisabled}
        >
          {children}
        </SliderContent>

        {showArrows && slidesCount > 1 && isShiftNextPossible && slidesCount > slidesPerPage && (
          <>
            <PrevArrow
              isActive={gallery?.circularScroll || isShiftPrevPossible(-1)}
              position={arrowPosition}
              onClick={(e) => {
                e.stopPropagation()
                shiftSlider(-1)
              }}
            />
            <NextArrow
              isActive={gallery?.circularScroll || isShiftNextPossible(1)}
              position={arrowPosition}
              onClick={(e) => {
                e.stopPropagation()
                shiftSlider(1)
              }}
            />
          </>
        )}
      </Wrapper>
    </SliderContextProvider>
  )
}

const Wrapper = styled.div<{ showOverflowedSlides: boolean }>`
  position: relative;
  height: 100%;
  overflow: ${({ showOverflowedSlides }) => (showOverflowedSlides ? 'visible' : 'hidden')};
  padding-top: 5px;
  padding-bottom: 10px;
  border-radius: ${getProjectName() === 'avito' && '12px'};
`
